/**
 * GridCrash — Mobile Action Bar
 *
 * Fixed, floating Dig/Flag toggle pinned above the iOS home indicator
 * (safe-area-inset-bottom). Replaces the old 500ms long-press flag
 * gesture on touch devices. Hidden on mouse-primary devices, where
 * right-click flagging remains the paradigm.
 */

import { hapticLight } from "../game/haptics.js";

export type TapMode = "dig" | "flag";

export class ActionBar {
  private root: HTMLElement;
  private digBtn: HTMLButtonElement;
  private flagBtn: HTMLButtonElement;
  private mode: TapMode = "dig";
  private onModeChange: (mode: TapMode) => void;

  constructor(onModeChange: (mode: TapMode) => void) {
    this.onModeChange = onModeChange;

    this.root = document.createElement("div");
    this.root.className = "action-bar";
    this.root.setAttribute("role", "radiogroup");
    this.root.setAttribute("aria-label", "Tap mode");

    this.digBtn  = this.buildButton("dig",  "⛏️", "Dig");
    this.flagBtn = this.buildButton("flag", "🚩", "Flag");
    this.root.append(this.digBtn, this.flagBtn);
    document.body.appendChild(this.root);

    this.render();
  }

  /** Show/hide the whole bar (touch devices only). */
  setVisible(visible: boolean): void {
    this.root.classList.toggle("action-bar--visible", visible);
    document.body.classList.toggle("has-action-bar", visible);
  }

  getMode(): TapMode {
    return this.mode;
  }

  /** Programmatic reset (e.g. back to Dig on every new game). */
  setMode(mode: TapMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.render();
    this.onModeChange(mode);
  }

  private buildButton(mode: TapMode, icon: string, label: string): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `action-bar__btn action-bar__btn--${mode}`;
    btn.setAttribute("role", "radio");
    btn.innerHTML = `<span class="action-bar__icon" aria-hidden="true">${icon}</span>${label}`;
    btn.addEventListener("click", () => {
      if (this.mode !== mode) hapticLight();
      this.setMode(mode);
    });
    return btn;
  }

  private render(): void {
    const isFlag = this.mode === "flag";
    this.digBtn.classList.toggle("action-bar__btn--active", !isFlag);
    this.flagBtn.classList.toggle("action-bar__btn--active", isFlag);
    this.digBtn.setAttribute("aria-checked", String(!isFlag));
    this.flagBtn.setAttribute("aria-checked", String(isFlag));
  }
}
